/**
 * The device benchmark's render phase (`client-benchmark`: "The render phase measures the
 * shipped renderer at a pinned animation mode"; design **D-E**).
 *
 * ## Structure — pure engine vs. GPU-real wiring (D-086)
 *
 * `CanvasRenderer` cannot be driven end to end under test: jsdom has no WebGL, so its `init()`
 * rejects before a single frame draws (see `renderer-destroy-safety.test.tsx`'s header). Every
 * other pure/Pixi-free seam in `canvas/` is unit-tested directly and composed into the GPU-real
 * class only at the end — this module follows the same split:
 *
 * - **Pure, unit-tested without a GPU:** {@link computeFramePacing} (percentiles, sample count,
 *   warm-up exclusion — `client-benchmark`'s "distribution, not only extremes" and "warm-up
 *   frames are excluded" requirements), {@link RenderFrameSampler} (the warm-up/measured frame
 *   bucketing), {@link runMeasuredRenderWindow} (the scheduling + visibility-invalidation +
 *   failure-path state machine — driven by the injected `Clock`/`FrameScheduler`/
 *   `VisibilitySource`, so `frame-sequence-driver.ts` exercises it exactly like `loop.test.ts`
 *   exercises `RenderLoop`), {@link benchmarkSceneFrame} (the synthetic 1000-bee scene with
 *   positions that genuinely advance), and {@link createBenchmarkDegradationMonitor} (design
 *   D-E's never-degrading monitor).
 * - **GPU-real, exercised manually/on-device only:** {@link runBenchmarkRenderPhase}, which
 *   constructs a real `CanvasRenderer` and feeds it the synthetic scene frame by frame. It is a
 *   thin composition of the pieces above; nothing in it is unit-tested, matching the existing
 *   `CanvasRenderer` class body.
 *
 * ## What "pinned" means here
 *
 * `resolveDegradationMonitor`'s injection point (design D-E, `canvas/renderer.ts`) is how the
 * animation mode is held fixed: {@link createBenchmarkDegradationMonitor} builds a
 * `DegradationMonitor` with no `frameGuard` and is never fed a `truncated`/over-cap signal by
 * this module, so it is structurally incapable of degrading — not merely one that happens not
 * to on a given run. {@link BENCHMARK_RENDER_CAMERA_SCALE} is asserted above
 * `symbols.SYMBOL_ZOOM_THRESHOLD` for the same reason: a scene below it would silently switch to
 * the cheaper batched-dot draw path and measure something else.
 */

import type { Camera } from '../../canvas/projection';
import type { WorldSnapshot } from '../../net/wire/connection';
import { ActionClass, Tier } from '../../net/wire/messages';
import { DegradationMonitor, mode as animationModeFor } from '../../canvas/animation-mode';
import { performanceClock, rafFrameScheduler, type Clock, type FrameScheduler } from '../../canvas/frame-scheduler';
import { CanvasRenderer } from '../../canvas/renderer';
import { SYMBOL_ZOOM_THRESHOLD, symbolModeForScale } from '../../canvas/symbols';
import { phaseFailed, type FramePacing, type RenderPhaseResult } from './report';

// --------------------------------------------------------------------------------------------
// The synthetic scene (task 6.5): 1000 bees, close tier, positions genuinely advancing.
// --------------------------------------------------------------------------------------------

/** Bees in the benchmark's synthetic scene (`client-benchmark`: "a close-tier field of 1000
 * bees"). */
export const BENCHMARK_RENDER_BEE_COUNT = 1000;

/**
 * The synthetic camera scale the render phase measures at. Asserted (in
 * `benchmark-render-phase.test.ts`) to sit above {@link SYMBOL_ZOOM_THRESHOLD} — below it the
 * close tier draws bees as batched colour dots (`symbols.ts`) instead of sprites, a materially
 * cheaper path, and the phase would measure a different renderer than it claims to.
 */
export const BENCHMARK_RENDER_CAMERA_SCALE = 1.4;

/** Frames spent warming up (shader/texture upload after `init()`) — excluded from the reported
 * statistics but recorded, never silently discarded (`client-benchmark`: "warm-up frames are
 * excluded from the statistics"). */
export const BENCHMARK_WARMUP_FRAME_COUNT = 30;

/** Measured frames collected before the phase settles. Sample count, not a duration: the
 * instrument runs on unknown hardware, so a frame COUNT is the only target that is comparable
 * across a fast device (reached quickly) and a slow one (reached slowly) alike. */
export const BENCHMARK_SAMPLE_TARGET = 300;

/** One bee's synthetic position for a given frame. Structurally the minimal shape
 * `buildBenchmarkWorldSnapshot` needs — never the full wire `BeeRecord`, since the scene has no
 * wire origin. */
export interface BenchmarkBeeSample {
  readonly id: number;
  readonly position: readonly [number, number];
}

/** World half-extent (u) the synthetic bees orbit within, chosen only to keep every position
 * comfortably inside the field's coordinate space — the benchmark scene has no relationship to
 * any particular match's field size. */
const BENCHMARK_SCENE_RADIUS = 400;

/** Per-frame angular step (radians) each bee advances around its own circle. Deliberately well
 * above `facing.ts`'s `DEFAULT_MIN_SPEED` threshold at this radius, so every bee's derived
 * heading is live motion, never a held/default value. */
const BENCHMARK_ANGULAR_STEP = 0.05;

/**
 * Produces one frame's bee positions for the benchmark's synthetic scene (task 6.5). Each bee
 * `id` (0-indexed) sits on its own circle — at a distinct starting angle `2π · id / beeCount`,
 * radius scaled by `0.3 + 0.7 · (id mod 7) / 6` so the field reads as spread rather than one
 * ring — and advances `BENCHMARK_ANGULAR_STEP` radians per `frameIndex`. Pure: the same
 * `(frameIndex, beeCount)` always yields the same positions, and positions differ between any
 * two distinct `frameIndex` values for every bee (the property `benchmark-render-phase.test.ts`
 * asserts directly, and the one that guards against `facing.ts`'s uniform-heading fallback).
 */
export function benchmarkSceneFrame(
  frameIndex: number,
  beeCount: number = BENCHMARK_RENDER_BEE_COUNT,
): BenchmarkBeeSample[] {
  const samples: BenchmarkBeeSample[] = [];
  for (let id = 0; id < beeCount; id++) {
    const startAngle = (2 * Math.PI * id) / beeCount;
    const radius = BENCHMARK_SCENE_RADIUS * (0.3 + (0.7 * (id % 7)) / 6);
    const angle = startAngle + BENCHMARK_ANGULAR_STEP * frameIndex;
    samples.push({
      id,
      position: [radius * Math.cos(angle), radius * Math.sin(angle)],
    });
  }
  return samples;
}

/**
 * Wraps {@link benchmarkSceneFrame} into a full close-tier `WorldSnapshot` — the shape
 * `CanvasRenderer.render` accepts — with every field a `WorldSnapshot` structurally requires but
 * the benchmark has no use for left empty/zero. `truncated`/`totalCount` are always the honest
 * "not truncated, N bees" values: the benchmark never wants the degradation monitor's
 * cap-pressure arm to trip either (design D-E pins the mode two ways, not one).
 */
export function buildBenchmarkWorldSnapshot(
  frameIndex: number,
  beeCount: number = BENCHMARK_RENDER_BEE_COUNT,
): WorldSnapshot {
  const bees = new Map(
    benchmarkSceneFrame(frameIndex, beeCount).map((sample) => [
      sample.id,
      {
        id: sample.id,
        position: [sample.position[0], sample.position[1]] as [number, number],
        facing: 0,
        action: ActionClass.Forage,
        gathering: false,
        colonyId: 0,
        typeId: 0,
        health: 255,
        nectar: 0,
      },
    ]),
  );
  return {
    tier: Tier.Close,
    tick: frameIndex,
    seq: BigInt(frameIndex),
    bees,
    blobs: new Map(),
    queens: new Map(),
    patches: new Map(),
    ruins: new Map(),
    dances: new Map(),
    danceRadius: 40,
    cells: new Map(),
    cellsTruncated: false,
    truncated: false,
    totalCount: beeCount,
  };
}

// --------------------------------------------------------------------------------------------
// The pinned degradation monitor (design D-E, task 6.4).
// --------------------------------------------------------------------------------------------

/**
 * Builds the degradation monitor the render phase injects into `CanvasRenderer` (design D-E).
 * A `DegradationMonitor` with no `frameGuard` never trips on frame timing, and this module never
 * feeds it a `truncated`/over-cap signal — so, unlike the production monitor
 * (`resolveDegradationMonitor`'s default), it is structurally incapable of degrading. That is
 * the point: a benchmark's workload must not get cheaper because the device it is measuring is
 * slow.
 */
export function createBenchmarkDegradationMonitor(clock: Clock): DegradationMonitor {
  return new DegradationMonitor(clock);
}

// --------------------------------------------------------------------------------------------
// Frame-pacing statistics (tasks 6.6/6.7): warm-up exclusion, percentiles, sample count.
// --------------------------------------------------------------------------------------------

/**
 * Computes the reported frame-pacing distribution from measured (post-warm-up) frame intervals.
 * Percentiles use nearest-rank (`ceil(p/100 · n) − 1`, clamped into range) over the ascending-
 * sorted intervals — a single deterministic definition, not a statistical claim to precision the
 * sample size doesn't support. `minFps`/`maxFps`/`meanFps` are derived from the slowest/fastest/
 * mean interval respectively; a non-positive interval (a degenerate/zero-delta sample) reports
 * `0` fps for that figure rather than `Infinity`, which would not survive JSON.
 *
 * Pure and total: `measuredIntervalsMs` may be empty (reports all-zero figures with
 * `sampleCount: 0`) — {@link RenderFrameSampler} never calls this until at least one sample
 * exists, but the function itself never assumes it.
 */
export function computeFramePacing(
  measuredIntervalsMs: readonly number[],
  warmupFrameCount: number,
  warmupMs: number,
): FramePacing {
  const n = measuredIntervalsMs.length;
  const sorted = [...measuredIntervalsMs].sort((a, b) => a - b);
  const minIntervalMs = sorted[0] ?? 0;
  const maxIntervalMs = sorted[n - 1] ?? 0;
  const meanIntervalMs = n === 0 ? 0 : sorted.reduce((sum, v) => sum + v, 0) / n;

  const percentile = (p: number): number => {
    if (n === 0) return 0;
    const idx = Math.min(n - 1, Math.max(0, Math.ceil((p / 100) * n) - 1));
    return sorted[idx];
  };
  const fpsFrom = (intervalMs: number): number => (intervalMs > 0 ? 1000 / intervalMs : 0);

  return {
    sampleCount: n,
    warmupFrameCount,
    warmupMs,
    minFps: fpsFrom(maxIntervalMs), // the slowest frame is the lowest fps
    maxFps: fpsFrom(minIntervalMs), // the fastest frame is the highest fps
    meanFps: fpsFrom(meanIntervalMs),
    p50FrameMs: percentile(50),
    p95FrameMs: percentile(95),
    p99FrameMs: percentile(99),
  };
}

/**
 * Buckets one presented frame's timestamp into warm-up or measured, and settles once enough
 * measured samples have been collected (tasks 6.6/6.7). The first frame ever recorded has no
 * prior timestamp to diff against, so its interval is `0` — always a warm-up frame in practice
 * (`BENCHMARK_WARMUP_FRAME_COUNT` > 0), so this degenerate first sample never reaches the
 * reported statistics.
 */
export class RenderFrameSampler {
  private lastNowMs: number | null = null;
  private frameIndex = 0;
  private warmupMsAccum = 0;
  private readonly measuredIntervalsMs: number[] = [];
  private settledPacing: FramePacing | null = null;

  constructor(
    private readonly warmupFrameCount: number,
    private readonly sampleTarget: number,
  ) {}

  /** The computed statistics once settled, else `null`. */
  get framePacing(): FramePacing | null {
    return this.settledPacing;
  }

  /**
   * Records one presented frame. Returns `true` exactly on the call that causes the sampler to
   * settle (so a caller reacts once, not on every subsequent call — calling again after settling
   * is a no-op that returns `false`).
   */
  recordFrame(nowMs: number): boolean {
    if (this.settledPacing !== null) {
      return false;
    }
    const intervalMs = this.lastNowMs === null ? 0 : nowMs - this.lastNowMs;
    this.lastNowMs = nowMs;

    if (this.frameIndex < this.warmupFrameCount) {
      this.warmupMsAccum += intervalMs;
    } else {
      this.measuredIntervalsMs.push(intervalMs);
    }
    this.frameIndex++;

    if (this.measuredIntervalsMs.length >= this.sampleTarget) {
      this.settledPacing = computeFramePacing(
        this.measuredIntervalsMs,
        this.warmupFrameCount,
        this.warmupMsAccum,
      );
      return true;
    }
    return false;
  }
}

// --------------------------------------------------------------------------------------------
// Visibility invalidation (`client-benchmark`: "A measurement disturbed by the environment is
// invalidated, not published"; tasks 6.9/6.10).
// --------------------------------------------------------------------------------------------

/** The machine-readable reason recorded on `RenderPhaseInvalidated` when the page loses
 * visibility mid-window. */
export const VISIBILITY_LOST_REASON = 'visibility_lost';

/**
 * A source of the page's visibility state, abstracted so the render phase's invalidation logic
 * is testable without a real `document` (`frame-sequence-driver.ts`'s `FakeVisibilitySource`
 * implements this shape). Production wraps `document.hidden` / `visibilitychange`.
 */
export interface VisibilitySource {
  readonly hidden: boolean;
  /** Subscribes to visibility changes; returns an unsubscribe function. */
  onChange(callback: (hidden: boolean) => void): () => void;
}

/** The production `VisibilitySource`: `document.hidden` + `visibilitychange`. Safe to call with
 * no `document` (SSR/node) — reports permanently visible and never notifies, rather than
 * throwing. */
export function documentVisibilitySource(): VisibilitySource {
  return {
    get hidden(): boolean {
      return typeof document !== 'undefined' && document.hidden;
    },
    onChange(callback: (hidden: boolean) => void): () => void {
      if (typeof document === 'undefined') {
        return () => {};
      }
      const handler = (): void => callback(document.hidden);
      document.addEventListener('visibilitychange', handler);
      return () => document.removeEventListener('visibilitychange', handler);
    },
  };
}

// --------------------------------------------------------------------------------------------
// The measured window: scheduling + visibility invalidation + the failure path (tasks 6.9/6.10).
// --------------------------------------------------------------------------------------------

export interface MeasuredRenderWindowConfig {
  readonly clock: Clock;
  readonly scheduler: FrameScheduler;
  readonly visibility: VisibilitySource;
  readonly warmupFrameCount: number;
  readonly sampleTarget: number;
  readonly beeCount: number;
  readonly cameraScale: number;
  /** Called once per scheduled animation frame, in order, before that frame's timing is
   * recorded. In production this advances and presents the synthetic scene through the real
   * `CanvasRenderer`; a test passes a stub (or one that throws on a chosen frame to exercise the
   * failure path — `client-benchmark`: "a failed render phase is reported"). */
  readonly presentFrame: (frameIndex: number) => void;
  /** Called exactly once, whenever the window ends (settled, invalidated, or failed), so a
   * caller can tear down whatever `presentFrame` was driving. */
  readonly onEnd?: () => void;
}

/**
 * Runs the render phase's measured window (tasks 6.6–6.10): schedules frames one at a time over
 * the injected `Clock`/`FrameScheduler`, buckets each into warm-up/measured via a
 * {@link RenderFrameSampler}, and resolves once one of three things happens first —
 *
 * - **settled**: enough measured samples collected → `RenderPhaseSettled` carrying the pinned
 *   `beeCount`/`cameraScale` and the `full` animation mode (design D-E: the mode is pinned, not
 *   merely observed to hold, so it is reported as the constant it structurally is).
 * - **invalidated**: the page lost visibility (checked at the start of every frame, and via an
 *   immediate subscription so a mid-frame event is not missed) → `RenderPhaseInvalidated`,
 *   carrying no frame-rate figures at all — the type itself has no field for them.
 * - **failed**: `presentFrame` throws → `PhaseFailed`.
 *
 * Exactly one of these ends the window (subsequent scheduled callbacks after settling are inert
 * — `RenderFrameSampler.recordFrame` itself is a no-op once settled, and `onEnd` fires once).
 */
export function runMeasuredRenderWindow(
  config: MeasuredRenderWindowConfig,
): Promise<RenderPhaseResult> {
  return new Promise((resolve) => {
    const sampler = new RenderFrameSampler(config.warmupFrameCount, config.sampleTarget);
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = (result: RenderPhaseResult): void => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      config.onEnd?.();
      resolve(result);
    };

    const invalidate = (): void =>
      finish({ status: 'invalidated', reason: VISIBILITY_LOST_REASON });

    unsubscribe = config.visibility.onChange((hidden) => {
      if (hidden) invalidate();
    });

    if (config.visibility.hidden) {
      invalidate();
      return;
    }

    let frameIndex = 0;
    const runFrame = (): void => {
      if (settled) return;
      if (config.visibility.hidden) {
        invalidate();
        return;
      }
      try {
        config.presentFrame(frameIndex);
      } catch (err) {
        finish(phaseFailed(err instanceof Error ? err.message : 'render_frame_failed'));
        return;
      }
      const didSettle = sampler.recordFrame(config.clock.now());
      frameIndex++;
      if (didSettle) {
        const framePacing = sampler.framePacing;
        if (framePacing === null) {
          // Unreachable given `recordFrame`'s contract (it only returns true once
          // `framePacing` is set) — guarded rather than asserted with `!`, so a future
          // refactor that breaks the invariant fails loudly as a phase failure, not a crash.
          finish(phaseFailed('render_phase_settled_without_frame_pacing'));
          return;
        }
        finish({
          status: 'settled',
          framePacing,
          beeCount: config.beeCount,
          cameraScale: config.cameraScale,
          animationMode: animationModeFor(Tier.Close, false),
        });
        return;
      }
      config.scheduler.schedule(runFrame);
    };
    config.scheduler.schedule(runFrame);
  });
}

// --------------------------------------------------------------------------------------------
// GPU-real wiring — exercised manually/on-device only (D-086), not unit tested.
// --------------------------------------------------------------------------------------------

/**
 * Runs the render phase against the real, shipped renderer: constructs a `CanvasRenderer` over
 * `container` with the benchmark's never-degrading monitor injected (design D-E), presents the
 * synthetic 1000-bee scene frame by frame with positions genuinely advancing (task 6.5), and
 * measures frame pacing over the real production `Clock`/`FrameScheduler` until the phase
 * settles, is invalidated by a visibility loss, or fails. GPU-real; jsdom has no WebGL, so this
 * function is exercised manually/on-device only, exactly like `CanvasRenderer`'s own drawing
 * (D-086) — everything it composes ({@link runMeasuredRenderWindow},
 * {@link createBenchmarkDegradationMonitor}, {@link buildBenchmarkWorldSnapshot}) is unit-tested
 * independently above.
 */
export async function runBenchmarkRenderPhase(
  container: HTMLElement,
  tickRateHz: number,
): Promise<RenderPhaseResult> {
  const clock = performanceClock;
  const scheduler = rafFrameScheduler;
  const monitor = createBenchmarkDegradationMonitor(clock);
  const renderer = new CanvasRenderer(
    container,
    tickRateHz,
    undefined,
    clock,
    scheduler,
    monitor,
  );

  try {
    await renderer.init();
  } catch (err) {
    return phaseFailed(err instanceof Error ? err.message : 'render_init_failed');
  }

  const camera: Camera = { offsetX: 0, offsetY: 0, scale: BENCHMARK_RENDER_CAMERA_SCALE };
  let frameIndex = 0;

  return runMeasuredRenderWindow({
    clock,
    scheduler,
    visibility: documentVisibilitySource(),
    warmupFrameCount: BENCHMARK_WARMUP_FRAME_COUNT,
    sampleTarget: BENCHMARK_SAMPLE_TARGET,
    beeCount: BENCHMARK_RENDER_BEE_COUNT,
    cameraScale: BENCHMARK_RENDER_CAMERA_SCALE,
    presentFrame: () => {
      const world = buildBenchmarkWorldSnapshot(frameIndex, BENCHMARK_RENDER_BEE_COUNT);
      renderer.render(world, camera, 0, false);
      frameIndex++;
    },
    onEnd: () => renderer.destroy(),
  });
}

// Re-exported so a caller/test can assert the camera-scale/symbol-mode relationship (task 6.4)
// without importing `symbols.ts` directly for just that one check.
export { SYMBOL_ZOOM_THRESHOLD, symbolModeForScale };
